import _ from 'lodash';
import { getFreqConversionFactor, getAmplitudeConversionReference, convertToDbScale, convertFromDbScale, getAmplitudeTypeYAxisUnit } from '../utils';

/**
 * Safely extracts the frequency units from backend response or provides a fallback
 * @param {Object} response - Backend response object
 * @param {string} fallback - Fallback frequency unit (default: 'Hz')
 * @returns {string} - The frequency units (Hz, CPM, Orders)
 */
export const getResponseFrequencyUnits = (response, fallback = 'Hz') => {
  if (response && response.freq_units && typeof response.freq_units === 'string') {
    return response.freq_units;
  }
  return fallback;
};

/**
 * Determines the actual source unit for feature transformations
 * @param {Array} originalFeatures - Original features array (may be null/empty)
 * @param {string} fallbackSourceUnit - Fallback source unit to use
 * @param {string} defaultOriginalUnit - Default unit for original features (usually 'Hz')
 * @returns {string} - The determined source unit
 */
export const determineFeatureSourceUnit = (originalFeatures, fallbackSourceUnit, defaultOriginalUnit = 'Hz') => {
  if (originalFeatures && originalFeatures.length > 0) {
    // Original features from backend - check for explicit units
    const firstFeature = originalFeatures[0];
    return (firstFeature && firstFeature.freq_units) || defaultOriginalUnit;
  }
  // For current features, use the provided fallback source unit
  return fallbackSourceUnit;
};

/**
 * Gets the shaft speed for a specific point with fallback logic
 * This ensures consistent speed resolution across the codebase
 *
 * @param {Object} point - The point object that may contain shaft speed
 * @param {number} fallbackShaftSpeed - Fallback shaft speed if point doesn't have one
 * @returns {number|null} - The point's shaft speed or null if invalid
 */
export const getPointShaftSpeed = (point, fallbackShaftSpeed = null) => {
  const SHAFT_SPEED_THRESHOLD = 0.001;

  // Try to get speed from point
  const pointSpeed = (point && point.shaft_speed) || (point && point.speed) || (point && point.referenceShaftSpeed);

  if (typeof pointSpeed === 'number' && pointSpeed > SHAFT_SPEED_THRESHOLD) {
    return pointSpeed;
  }

  // Fallback to provided fallback speed
  if (typeof fallbackShaftSpeed === 'number' && fallbackShaftSpeed > SHAFT_SPEED_THRESHOLD) {
    return fallbackShaftSpeed;
  }

  return null;
};

/**
 * Determines the most appropriate shaft speed reference for consistent transformations
 * This function is used for UI elements that need a single reference (e.g., cursors, shared markers)
 * NOTE: For spectral data normalization in Orders mode, use getPointShaftSpeed() per point instead
 *
 * @param {Array} points - Array of overlay points that may contain individual shaft speeds
 * @param {number} fallbackShaftSpeed - Fallback shaft speed from external source
 * @returns {number} - Consistent shaft speed to use for all transformations
 */
export const getConsistentShaftSpeed = (points, fallbackShaftSpeed) => {
  // Priority 1: Use the most common non-zero shaft speed from overlay points
  if (points && Array.isArray(points) && points.length > 0) {
    const validSpeeds = points
      .map(point => point.shaft_speed || point.speed || point.referenceShaftSpeed)
      .filter(speed => typeof speed === 'number' && speed > 0.001);

    if (validSpeeds.length > 0) {
      // For multiple points with different speeds, use the first speed to ensure
      // all markers are calculated using the same reference point
      if (validSpeeds.length > 1) {
        // Check if all speeds are reasonably similar (within 1% tolerance)
        const firstSpeed = validSpeeds[0];
        const tolerance = firstSpeed * 0.01;
        const allSimilar = validSpeeds.every(speed =>
          Math.abs(speed - firstSpeed) <= tolerance
        );

        if (allSimilar) {
          // Use average if all speeds are similar
          return validSpeeds.reduce((sum, speed) => sum + speed, 0) / validSpeeds.length;
        }
        // Use first speed as reference when speeds differ to maintain consistency
        return firstSpeed;
      }
      // Single speed, use it directly
      return validSpeeds[0];
    }
  }

  // Priority 2: Use fallback shaft speed if valid
  if (typeof fallbackShaftSpeed === 'number' && fallbackShaftSpeed > 0.001) {
    return fallbackShaftSpeed;
  }

  // Priority 3: Default to 1 Hz for Orders mode calculations (prevents division by zero)
  return 1;
};

/**
 * Validates whether an amplitude type change is valid for a given point
 * Prevents invalid conversions that could result in incorrect data
 *
 * @param {Object} point - The overlay point to validate
 * @param {string} newAmplitudeType - The target amplitude type
 * @param {string} targetYUnit - The target Y-axis unit
 * @returns {boolean} - Whether the conversion is valid
 */
const isAmplitudeTypeChangeValid = (point, newAmplitudeType, targetYUnit) => {
  // Always allow if we have original data to work from
  if (point.originalData && point.originalResponse) {
    return true;
  }

  // Check for compatible amplitude type transitions
  const currentYUnit = point.yUnit;
  if (!currentYUnit || !targetYUnit) {
    return false;
  }

  // Define compatible conversion paths
  const compatibleConversions = {
    velocity: ['acceleration', 'demod', 'db'],
    acceleration: ['velocity', 'demod', 'db'],
    demod: ['velocity', 'acceleration', 'db'],
    db: ['velocity', 'acceleration', 'demod']
  };

  const currentBase = currentYUnit.toLowerCase().replace(/\s.*$/, ''); // Remove units, keep base type
  const targetBase = targetYUnit.toLowerCase().replace(/\s.*$/, '');

  return (compatibleConversions[currentBase] && compatibleConversions[currentBase].includes(targetBase)) || currentBase === targetBase;
};

/**
 * Validates a transformed point for data integrity
 * Checks for common issues that could arise during transformations
 *
 * @param {Object} point - The transformed point to validate
 * @returns {boolean} - Whether the point is valid
 */
const validateTransformedPoint = (point) => {
  // Check for required properties
  if (!point.spectrum_data || !Array.isArray(point.spectrum_data)) {
    return false;
  }

  // Check for valid spectrum data
  if (point.spectrum_data.length === 0) {
    return false;
  }

  // Check for NaN or invalid values in spectrum data
  const hasInvalidData = point.spectrum_data.some(d =>
    !d || typeof d.x !== 'number' || typeof d.y !== 'number' ||
    Number.isNaN(d.x) || Number.isNaN(d.y) || !Number.isFinite(d.x) || !Number.isFinite(d.y)
  );

  if (hasInvalidData) {
    return false;
  }

  // Check for valid units
  if (!point.xUnit || !point.yUnit) {
    return false;
  }

  // All checks passed
  return true;
};

/**
 * Transforms spectrum data points from one frequency unit to another
 * @param {Array} dataPoints - Array of spectrum data points with x,y coordinates
 * @param {string} sourceUnit - Source frequency unit (Hz, CPM, Orders)
 * @param {string} targetUnit - Target frequency unit (Hz, CPM, Orders)
 * @param {number} shaftSpeed - Shaft speed value in the original/backend frequency unit
 * @param {Array} [originalData] - Original unmodified data points (for accurate conversion)
 * @param {string} [originalDataUnit] - Frequency unit of originalData
 * @param {string} [shaftSpeedUnit] - Frequency unit of shaftSpeed
 * @returns {Array} - Transformed data points
 */
export const transformSpectrumData = (
  dataPoints,
  sourceUnit,
  targetUnit,
  shaftSpeed,
  originalData = null,
  originalDataUnit = null,
  shaftSpeedUnit = null
) => {
  // Input validation
  if (!dataPoints || !Array.isArray(dataPoints)) return [];
  if (sourceUnit === targetUnit) return _.cloneDeep(dataPoints);
  if (!sourceUnit || !targetUnit) return _.cloneDeep(dataPoints);

  const resolvedOriginalDataUnit = originalDataUnit || sourceUnit || 'Hz';
  const resolvedShaftSpeedUnit = shaftSpeedUnit || resolvedOriginalDataUnit || sourceUnit || 'Hz';

  if (typeof shaftSpeed !== 'number' || shaftSpeed <= 0) {
    if (targetUnit === 'Orders' || sourceUnit === 'Orders') {
      return _.cloneDeep(dataPoints);
    }
  }

  try {
    // Get conversion factor
    const factor = getFreqConversionFactor(sourceUnit, targetUnit, shaftSpeed, resolvedShaftSpeedUnit);

    if (!Number.isFinite(factor) || factor <= 0) {
      return _.cloneDeep(dataPoints);
    }

    // If we have original data, transform directly from it for accuracy
    if (originalData && Array.isArray(originalData) && originalData.length > 0) {
      return originalData.map((d, index) => {
        if (!d || typeof d.x !== 'number' || Number.isNaN(d.x) || !Number.isFinite(d.x)) {
          return d;
        }

        const newX = d.x * getFreqConversionFactor(
          resolvedOriginalDataUnit,
          targetUnit,
          shaftSpeed,
          resolvedShaftSpeedUnit
        );

        if (!Number.isFinite(newX)) {
          return d;
        }

        const currentPoint = dataPoints[index];
        return {
          ...d,
          x: newX,
          y: currentPoint && typeof currentPoint.y === 'number' ? currentPoint.y : d.y
        };
      });
    }

    // Otherwise transform from current data
    return dataPoints.map((d) => {
      if (!d || typeof d.x !== 'number' || Number.isNaN(d.x) || !Number.isFinite(d.x)) {
        return d;
      }

      const newX = d.x * factor;

      if (!Number.isFinite(newX)) {
        return d;
      }

      return {
        ...d,
        x: newX
      };
    });
  } catch (error) {
    return _.cloneDeep(dataPoints);
  }
};

/**
 * Transforms spectrum features/markers from one frequency unit to another
 * @param {Array} features - Array of feature objects with frequency properties
 * @param {string} sourceUnit - Source frequency unit (Hz, CPM, Orders) - should match actual backend data units
 * @param {string} targetUnit - Target frequency unit (Hz, CPM, Orders)
 * @param {number} shaftSpeed - Shaft speed value in the original/backend frequency unit
 * @param {Array} [originalFeatures] - Original unmodified features (for accurate conversion)
 * @param {string} [originalFeaturesUnit] - Frequency unit of originalFeatures
 * @param {string} [shaftSpeedUnit] - Frequency unit of shaftSpeed
 * @returns {Array} - Transformed features
 */
export const transformSpectrumFeatures = (
  features,
  sourceUnit,
  targetUnit,
  shaftSpeed,
  originalFeatures = null,
  originalFeaturesUnit = null,
  shaftSpeedUnit = null
) => {
  if (!features || !Array.isArray(features)) return [];
  if (sourceUnit === targetUnit) return _.cloneDeep(features);

  // Determine which source to use for conversion
  const sourceFeatures = originalFeatures || features;

  // BUGFIX: Determine the actual source unit of the features correctly using centralized logic
  const actualSourceUnit = determineFeatureSourceUnit(
    originalFeatures,
    sourceUnit,
    originalFeaturesUnit || sourceUnit || 'Hz'
  );
  const resolvedShaftSpeedUnit = shaftSpeedUnit || actualSourceUnit || sourceUnit || 'Hz';

  const factor = getFreqConversionFactor(actualSourceUnit, targetUnit, shaftSpeed, resolvedShaftSpeedUnit);

  // BUGFIX: Add validation for conversion factor
  if (!Number.isFinite(factor) || factor <= 0) {
    console.warn('Invalid conversion factor in transformSpectrumFeatures', {
      actualSourceUnit,
      targetUnit,
      shaftSpeed,
      factor,
      featuresCount: sourceFeatures.length
    });
    return _.cloneDeep(features);
  }

  return sourceFeatures.map((feature) => {
    const newFeature = _.cloneDeep(feature);

    // Transform all frequency-related properties with validation
    if (newFeature.frequency && typeof newFeature.frequency === 'number') {
      const newFreq = feature.frequency * factor;
      if (Number.isFinite(newFreq)) {
        newFeature.frequency = newFreq;
      }
    }
    if (newFeature.frequency_start && typeof newFeature.frequency_start === 'number') {
      const newFreqStart = feature.frequency_start * factor;
      if (Number.isFinite(newFreqStart)) {
        newFeature.frequency_start = newFreqStart;
      }
    }
    if (newFeature.frequency_end && typeof newFeature.frequency_end === 'number') {
      const newFreqEnd = feature.frequency_end * factor;
      if (Number.isFinite(newFreqEnd)) {
        newFeature.frequency_end = newFreqEnd;
      }
    }

    // Update unit information
    newFeature.freq_units = targetUnit;

    return newFeature;
  });
};

/**
 * Transforms selected points with their spectrum data and features
 * @param {Array} points - Array of selected points with spectrum_data and spectrumFeatures
 * @param {string} sourceUnit - Source frequency unit (Hz, CPM, Orders)
 * @param {string} targetUnit - Target frequency unit (Hz, CPM, Orders)
 * @param {number} shaftSpeed - Default shaft speed in the original/backend frequency unit
 * @param {string} [shaftSpeedUnit] - Frequency unit of shaftSpeed
 * @returns {Array} - Transformed points
 */
export const transformSelectedPoints = (points, sourceUnit, targetUnit, shaftSpeed, shaftSpeedUnit = null) => {
  // Input validation
  if (!points || !Array.isArray(points)) return [];
  if (sourceUnit === targetUnit) return _.cloneDeep(points);
  if (!sourceUnit || !targetUnit) return _.cloneDeep(points);

  const defaultShaftSpeedUnit = shaftSpeedUnit || sourceUnit || 'Hz';

  try {
    // For Orders mode with overlay, we need per-point normalization
    // Each spectrum should use its own shaft speed so Order=1 represents 1X for that spectrum
    // This enables proper amplitude comparison across different RPMs

    // Check if we have any valid speeds for Orders mode
    if (targetUnit === 'Orders' || sourceUnit === 'Orders') {
      const hasValidSpeed = points.some((point) => {
        const speed = getPointShaftSpeed(point, shaftSpeed);
        return speed !== null;
      });

      if (!hasValidSpeed) {
        // No valid speeds, return points unchanged
        return _.cloneDeep(points);
      }
    }

    return points.map((point, pointIndex) => {
      try {
        // Skip points without spectrum data
        if (!point.spectrum_data) {
          return point;
        }

        // Validate point structure
        if (!Array.isArray(point.spectrum_data) || point.spectrum_data.length === 0) {
          return point;
        }

        const pointShaftSpeed = getPointShaftSpeed(point, shaftSpeed);
        const pointShaftSpeedUnit = getResponseFrequencyUnits(
          point.originalResponse,
          defaultShaftSpeedUnit
        );

        // If we need Orders conversion but point has no valid speed, skip transformation
        if ((targetUnit === 'Orders' || sourceUnit === 'Orders') && !pointShaftSpeed) {
          return point;
        }

        // Always use original data for transformations to prevent cascading errors
        const useOriginalData = point.originalData && Array.isArray(point.originalData);
        const useOriginalFeatures = point.originalFeatures && point.originalFeatures.items;

        // Determine Y-axis units for proper conversion
        let transformedData;
        if (useOriginalData) {
          // ENHANCED: Start with pristine original data to ensure accuracy
          transformedData = _.cloneDeep(point.originalData);

          // Get the original Y-axis unit from the response or fallback to current point's yUnit
          const originalYUnit = (point.originalResponse && point.originalResponse.amp_units) || 'velocity';
          const currentYUnit = point.yUnit || originalYUnit;

          // Apply Y-axis conversion first if the current Y-axis unit differs from original
          if (currentYUnit && originalYUnit && currentYUnit !== originalYUnit) {
            const reference = getAmplitudeConversionReference(originalYUnit, currentYUnit);
            if (reference) {
              if (currentYUnit.toLowerCase() === 'db' && originalYUnit.toLowerCase() !== 'db') {
                // Convert to dB
                transformedData = transformedData.map(d => ({
                  x: d.x,
                  y: convertToDbScale(d.y, reference)
                }));
              } else if (currentYUnit.toLowerCase() !== 'db' && originalYUnit.toLowerCase() === 'db') {
                // Convert from dB
                transformedData = transformedData.map(d => ({
                  x: d.x,
                  y: convertFromDbScale(d.y, reference)
                }));
              }
            }
          }

          // Get the actual frequency units from the backend response, fallback to Hz if not available
          const originalFreqUnits = getResponseFrequencyUnits(point.originalResponse, 'Hz');

          // For Orders mode, always transform from the original frequency units to ensure consistency
          if (targetUnit === 'Orders') {
            transformedData = transformSpectrumData(
              transformedData,
              originalFreqUnits, // Use actual backend frequency units
              targetUnit,
              pointShaftSpeed,
              null, // Don't use originalData parameter since we've already processed it
              null,
              pointShaftSpeedUnit
            );
          } else {
            // For other units, use normal transformation path
            transformedData = transformSpectrumData(
              transformedData,
              originalFreqUnits, // Use actual backend frequency units
              targetUnit,
              pointShaftSpeed,
              null,
              null,
              pointShaftSpeedUnit
            );
          }
        } else {
          // ENHANCED: Fallback when original data is not available
          // Create a safe copy to avoid modifying the original point data
          const safeSourceData = _.cloneDeep(point.spectrum_data);
          transformedData = transformSpectrumData(
            safeSourceData,
            sourceUnit,
            targetUnit,
            pointShaftSpeed,
            null,
            null,
            pointShaftSpeedUnit
          );

          // Store this transformation as original data if it doesn't exist
          if (!point.originalData) {
            point.originalData = _.cloneDeep(point.spectrum_data);
            point.originalResponse = {
              freq_units: sourceUnit,
              amp_units: point.yUnit || 'velocity'
            };
          }
        }

        // Convert features if present using consistent shaft speed
        let transformedFeatures = null;
        if (point.spectrumFeatures && point.spectrumFeatures.items) {
          // Get the actual frequency units from the backend response, fallback to Hz if not available
          const originalFreqUnits = getResponseFrequencyUnits(point.originalResponse, 'Hz');

          // BUGFIX: Determine feature source unit correctly using centralized logic
          const featureSourceUnit = useOriginalFeatures
            ? determineFeatureSourceUnit(
              point.originalFeatures && point.originalFeatures.items,
              point.xUnit || sourceUnit,
              originalFreqUnits
            )
            : (point.xUnit || sourceUnit);

          // ENHANCED: Comprehensive metadata preservation for overlay markers
          const featureItems = point.spectrumFeatures.items.map((feature) => {
            // Preserve all critical overlay association metadata
            const preservedMetadata = {};

            // Point association metadata
            if (feature.pointIndex !== undefined) preservedMetadata.pointIndex = feature.pointIndex;
            if (feature.originalPointIndex !== undefined) preservedMetadata.originalPointIndex = feature.originalPointIndex;
            if (feature.timestamp !== undefined) preservedMetadata.timestamp = feature.timestamp;

            // Overlay visual metadata
            if (feature.color !== undefined) preservedMetadata.color = feature.color;
            if (feature.overlayColor !== undefined) preservedMetadata.overlayColor = feature.overlayColor;
            if (feature.pointColor !== undefined) preservedMetadata.pointColor = feature.pointColor;

            // Marker identification metadata
            if (feature.marker !== undefined) preservedMetadata.marker = feature.marker;
            if (feature.markerType !== undefined) preservedMetadata.markerType = feature.markerType;
            if (feature.markerIndex !== undefined) preservedMetadata.markerIndex = feature.markerIndex;

            // Description and labeling metadata
            if (feature.description !== undefined) preservedMetadata.description = feature.description;
            if (feature.label !== undefined) preservedMetadata.label = feature.label;
            if (feature.isOverlayMarker !== undefined) preservedMetadata.isOverlayMarker = feature.isOverlayMarker;

            return {
              ...feature,
              ...preservedMetadata
            };
          });

          transformedFeatures = {
            items: transformSpectrumFeatures(
              featureItems,
              featureSourceUnit,
              targetUnit,
              pointShaftSpeed,
              useOriginalFeatures ? point.originalFeatures.items : null,
              originalFreqUnits,
              pointShaftSpeedUnit
            ).map((feature, featureIndex) => {
              // ENHANCED: Comprehensive metadata restoration after transformation
              const preservedMetadata = {};

              // Find the corresponding original feature using multiple matching strategies
              let originalFeature = null;

              // Strategy 1: Match by index (most reliable for ordered features)
              if (featureItems[featureIndex]) {
                originalFeature = featureItems[featureIndex];
              } else {
                // Strategy 2: Match by marker identifier
                originalFeature = featureItems.find(f => f.marker === feature.marker);
              }

              // Strategy 3: Match by name/description as fallback
              if (!originalFeature) {
                originalFeature = featureItems.find(f =>
                  (f.name === feature.name) || (f.description === feature.description)
                );
              }

              if (originalFeature) {
                // Restore all preserved metadata
                const metadataKeys = [
                  'pointIndex', 'originalPointIndex', 'timestamp',
                  'color', 'overlayColor', 'pointColor',
                  'marker', 'markerType', 'markerIndex',
                  'description', 'label', 'isOverlayMarker'
                ];

                metadataKeys.forEach((key) => {
                  if (originalFeature[key] !== undefined) {
                    preservedMetadata[key] = originalFeature[key];
                  }
                });
              }

              return {
                ...feature,
                ...preservedMetadata
              };
            })
          };
        }

        return {
          ...point,
          spectrum_data: transformedData,
          spectrumFeatures: transformedFeatures,
          // Preserve original data and features for future transformations
          originalData: point.originalData || point.spectrum_data,
          originalFeatures: point.originalFeatures || point.spectrumFeatures,
          xUnit: targetUnit,
          // ENHANCED: Preserve all overlay association metadata
          originalIndex: point.originalIndex !== undefined ? point.originalIndex : pointIndex,
          pointIndex: point.pointIndex !== undefined ? point.pointIndex : pointIndex,
          // Store the point's shaft speed used for transformation
          referenceShaftSpeed: pointShaftSpeed,
          // Preserve Y-axis unit during X-axis transformations
          yUnit: point.yUnit,
          // ENHANCED: Preserve overlay visual properties
          color: point.color,
          overlayColor: point.overlayColor,
          description: point.description,
          timestamp: point.timestamp,
          // Preserve transformation metadata
          lastTransformSource: sourceUnit,
          lastTransformTarget: targetUnit,
          lastTransformShaftSpeed: pointShaftSpeed
        };
      } catch (error) {
        return point; // Return original point if transformation fails
      }
    });
  } catch (error) {
    return _.cloneDeep(points);
  }
};

/**
 * Handles amplitude type changes for overlay points while maintaining coordinate system stability
 * This function ensures that when switching between velocity and acceleration, the overlay points
 * maintain their proper positioning on the trend chart by applying appropriate transformations.
 *
 * @param {Array} selectedPoints - Array of selected overlay points
 * @param {string} newAmplitudeType - New amplitude type (velocity, acceleration, etc.)
 * @param {Object} coordinateParams - Parameters for coordinate transformations
 * @param {string} coordinateParams.currentXUnit - Current X-axis unit (Hz, Orders, etc.)
 * @param {string} coordinateParams.currentYUnit - Current Y-axis unit
 * @param {number} coordinateParams.shaftSpeed - Shaft speed for transformations
 * @param {number} coordinateParams.unitSystem - Unit system (1 for metric, 2 for imperial)
 * @param {string} [coordinateParams.previousAmplitudeType] - Prior amp type (e.g. parent prop before change)
 * @returns {Array} - Transformed points with updated coordinate system
 */
export const handleAmplitudeTypeChange = (selectedPoints, newAmplitudeType, coordinateParams) => {
  if (!selectedPoints || !Array.isArray(selectedPoints)) return [];

  const {
    currentXUnit,
    currentYUnit,
    shaftSpeed,
    unitSystem = 1,
    previousAmplitudeType
  } = coordinateParams || {};

  // ENHANCED: Determine consistent Y-axis unit for all overlay points
  const correctYUnit = getAmplitudeTypeYAxisUnit(newAmplitudeType, unitSystem);
  const targetYUnit = correctYUnit || currentYUnit;

  return selectedPoints.map((point) => {
    // Skip points without spectrum data
    if (!point.spectrum_data) return point;

    // For amplitude type changes, we need to ensure coordinate system consistency
    // The key insight is that velocity and acceleration data have different scaling relationships
    // and the trend chart positioning must account for this when overlaying spectrum data

    const transformedPoint = { ...point };

    // ENHANCED: Validate amplitude type change compatibility
    if (!isAmplitudeTypeChangeValid(point, newAmplitudeType, targetYUnit)) {
      return point; // Return unchanged if conversion is not valid
    }

    // If the point has been refreshed with new amplitude type data,
    // ensure the coordinate transformations are applied correctly
    if (point.originalData && currentXUnit && targetYUnit) {
      let processedData = _.cloneDeep(point.originalData);

      // Apply Y-axis transformations if the target Y-unit differs from original
      const originalYUnit = (point.originalResponse && point.originalResponse.amp_units) || point.yUnit;
      if (targetYUnit !== originalYUnit) {
        const reference = getAmplitudeConversionReference(originalYUnit, targetYUnit);
        if (reference) {
          if (targetYUnit.toLowerCase() === 'db' && originalYUnit.toLowerCase() !== 'db') {
            processedData = processedData.map(d => ({
              x: d.x,
              y: convertToDbScale(d.y, reference)
            }));
          } else if (targetYUnit.toLowerCase() !== 'db' && originalYUnit.toLowerCase() === 'db') {
            processedData = processedData.map(d => ({
              x: d.x,
              y: convertFromDbScale(d.y, reference)
            }));
          }
        }
      }

      // Apply X-axis transformations if the current X-unit differs from original Hz
      const originalXUnit = (point.originalResponse && point.originalResponse.freq_units) || 'Hz';
      if (currentXUnit !== originalXUnit && shaftSpeed > 0.001) {
        const transformationResult = transformSpectrumCoordinates({
          sourceUnit: originalXUnit,
          targetUnit: currentXUnit,
          shaftSpeed,
          spectrumData: processedData,
          originalData: point.originalData,
          features: point.originalFeatures
        });
        processedData = transformationResult.spectrumData;

        // Update features with preserved marker metadata
        if (transformationResult.features && point.originalFeatures) {
          // Preserve marker association metadata during transformation
          if (point.spectrumFeatures && point.spectrumFeatures.items) {
            const preservedFeatures = transformationResult.features.items.map((feature, index) => {
              const originalFeature = point.spectrumFeatures.items[index];
              if (originalFeature) {
                return {
                  ...feature,
                  // Preserve critical marker metadata
                  pointIndex: originalFeature.pointIndex,
                  originalPointIndex: originalFeature.originalPointIndex,
                  timestamp: originalFeature.timestamp,
                  color: originalFeature.color || point.color,
                  marker: originalFeature.marker,
                  markerType: originalFeature.markerType
                };
              }
              return feature;
            });
            transformedPoint.spectrumFeatures = {
              ...transformationResult.features,
              items: preservedFeatures
            };
          } else {
            transformedPoint.spectrumFeatures = transformationResult.features;
          }
        }
      }

      // Update the spectrum data with properly transformed coordinates
      transformedPoint.spectrum_data = processedData;
    } else if (targetYUnit !== point.yUnit) {
      // Handle simple Y-axis unit changes when original data is not available
      const reference = getAmplitudeConversionReference(point.yUnit, targetYUnit);
      if (reference) {
        let convertedData = _.cloneDeep(point.spectrum_data);
        if (targetYUnit.toLowerCase() === 'db' && point.yUnit.toLowerCase() !== 'db') {
          convertedData = convertedData.map(d => ({
            x: d.x,
            y: convertToDbScale(d.y, reference)
          }));
        } else if (targetYUnit.toLowerCase() !== 'db' && point.yUnit.toLowerCase() === 'db') {
          convertedData = convertedData.map(d => ({
            x: d.x,
            y: convertFromDbScale(d.y, reference)
          }));
        }
        transformedPoint.spectrum_data = convertedData;
      }
    }

    // ENHANCED: Ensure coordinate system metadata is properly updated
    // This prevents accumulation of transformation errors during multiple amplitude type switches
    transformedPoint.xUnit = currentXUnit;
    transformedPoint.yUnit = targetYUnit; // Use the correct Y-axis unit for the new amplitude type
    transformedPoint.lastRefreshAmpType = newAmplitudeType;

    // Maintain reference shaft speed for consistent Orders mode calculations
    if (shaftSpeed > 0.001) {
      transformedPoint.referenceShaftSpeed = shaftSpeed;
    }

    // ENHANCED: Add transformation tracking for debugging and validation
    transformedPoint.amplitudeTransformHistory = transformedPoint.amplitudeTransformHistory || [];
    transformedPoint.amplitudeTransformHistory.push({
      timestamp: Date.now(),
      fromType: previousAmplitudeType || point.lastRefreshAmpType || 'unknown',
      toType: newAmplitudeType,
      fromYUnit: point.yUnit,
      toYUnit: targetYUnit,
      shaftSpeed
    });

    // ENHANCED: Validate the transformed point for consistency
    if (!validateTransformedPoint(transformedPoint)) {
      console.warn('Transformed point failed validation', transformedPoint);
    }

    return transformedPoint;
  });
};

/**
 * Comprehensive function to handle all spectrum-related transformations for X-axis unit changes
 * @param {Object} params - Transformation parameters
 * @param {string} params.sourceUnit - Source frequency unit (Hz, CPM, Orders)
 * @param {string} params.targetUnit - Target frequency unit (Hz, CPM, Orders)
 * @param {number} params.shaftSpeed - Shaft speed value in the original/backend frequency unit
 * @param {Array} [params.spectrumData] - Main spectrum data to transform
 * @param {Array} [params.originalData] - Original unmodified spectrum data
 * @param {string} [params.originalDataUnit] - Frequency unit of originalData
 * @param {Object} [params.features] - Spectrum features/markers to transform
 * @param {Array} [params.features.items] - Array of feature objects
 * @param {Object} [params.originalFeatures] - Original unmodified features
 * @param {string} [params.originalFeaturesUnit] - Frequency unit of originalFeatures
 * @param {Array} [params.selectedPoints] - Selected points to transform
 * @param {Array} [params.cursors] - Cursor positions to transform
 * @param {string} [params.shaftSpeedUnit] - Frequency unit of shaftSpeed
 * @returns {Object} - Object containing all transformed data
 */
export const transformSpectrumCoordinates = (params) => {
  const {
    sourceUnit,
    targetUnit,
    shaftSpeed,
    spectrumData,
    originalData,
    originalDataUnit,
    features,
    originalFeatures,
    originalFeaturesUnit,
    selectedPoints,
    cursors,
    shaftSpeedUnit
  } = params;
  const resolvedOriginalDataUnit = originalDataUnit || sourceUnit || 'Hz';
  const resolvedOriginalFeaturesUnit = originalFeaturesUnit || resolvedOriginalDataUnit;
  const resolvedShaftSpeedUnit = shaftSpeedUnit || resolvedOriginalDataUnit || sourceUnit || 'Hz';

  // Input validation
  if (!sourceUnit || !targetUnit) {
    console.warn('Invalid frequency units for coordinate transformation', { sourceUnit, targetUnit });
    return { spectrumData, features, selectedPoints, cursors };
  }

  // Skip if units are the same
  if (sourceUnit === targetUnit) {
    return {
      spectrumData,
      features,
      selectedPoints,
      cursors
    };
  }

  // ENVELOPE ALIGNMENT FIX: Enhanced Orders mode validation
  if ((sourceUnit === 'Orders' || targetUnit === 'Orders') && (!shaftSpeed || shaftSpeed <= 0.001)) {
    console.warn('Invalid shaft speed for Orders mode coordinate transformation', {
      sourceUnit,
      targetUnit,
      shaftSpeed
    });
    return { spectrumData, features, selectedPoints, cursors };
  }

  // Calculate the conversion factor
  const factor = getFreqConversionFactor(sourceUnit, targetUnit, shaftSpeed, resolvedShaftSpeedUnit);

  // ENVELOPE ALIGNMENT FIX: Validate conversion factor
  if (!Number.isFinite(factor) || factor <= 0) {
    console.warn('Invalid conversion factor for coordinate transformation', {
      factor,
      sourceUnit,
      targetUnit,
      shaftSpeed
    });
    return { spectrumData, features, selectedPoints, cursors };
  }

  // Transform main spectrum data if provided
  let transformedData = spectrumData;
  if (spectrumData) {
    if (Array.isArray(spectrumData[0])) {
      // Multiple datasets (array of arrays)
      transformedData = spectrumData.map((dataset, idx) =>
        transformSpectrumData(
          dataset,
          sourceUnit,
          targetUnit,
          shaftSpeed,
          originalData ? originalData[idx] : null,
          resolvedOriginalDataUnit,
          resolvedShaftSpeedUnit
        )
      );
    } else {
      // Single dataset (array of points)
      transformedData = transformSpectrumData(
        spectrumData,
        sourceUnit,
        targetUnit,
        shaftSpeed,
        originalData,
        resolvedOriginalDataUnit,
        resolvedShaftSpeedUnit
      );
    }
  }

  // Transform features if provided
  let transformedFeatures = features;
  if (features && features.items) {
    transformedFeatures = {
      items: transformSpectrumFeatures(
        features.items,
        sourceUnit,
        targetUnit,
        shaftSpeed,
        originalFeatures ? originalFeatures.items : null,
        resolvedOriginalFeaturesUnit,
        resolvedShaftSpeedUnit
      )
    };
  }

  // Transform selected points if provided
  let transformedPoints = selectedPoints;
  if (selectedPoints && Array.isArray(selectedPoints)) {
    transformedPoints = transformSelectedPoints(
      selectedPoints,
      sourceUnit,
      targetUnit,
      shaftSpeed,
      resolvedShaftSpeedUnit
    );
  }

  // Transform cursors if provided
  let transformedCursors = cursors;
  if (cursors && Array.isArray(cursors)) {
    transformedCursors = cursors.map((cursor) => {
      const newCursor = _.cloneDeep(cursor);

      // Transform cursor position
      if (typeof newCursor.x === 'number') newCursor.x *= factor;

      // Transform cursor text if it's a number
      if (typeof newCursor.text !== 'string' && newCursor.text !== undefined) {
        newCursor.text *= factor;
        newCursor.text = Math.round(newCursor.text);
      }

      return newCursor;
    });
  }

  return {
    spectrumData: transformedData,
    features: transformedFeatures,
    selectedPoints: transformedPoints,
    cursors: transformedCursors
  };
};
